import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useParams, useHistory } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import { API_BASE_URL } from "../../config";

const QUESTION_TIME_LIMIT = 60;

const PostTestExamQuiz = () => {
  const { trainingId, count } = useParams();
  const history = useHistory();

  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [timeLeft, setTimeLeft] = useState(QUESTION_TIME_LIMIT);

  const fetchQuestions = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/pre-test/${trainingId}/${count}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
      });

      const questionList = res.data.data || [];
      setQuestions(questionList);
      setCurrentIndex(0);
      setTimeLeft(QUESTION_TIME_LIMIT);
    } catch (err) {
      toast.error("Failed to load questions");
    }
  }, [count, trainingId]);

  useEffect(() => {
    fetchQuestions();
  }, [fetchQuestions]);

  useEffect(() => {
    if (!questions.length || loading) {
      return undefined;
    }

    if (timeLeft === 0) {
      if (currentIndex < questions.length - 1) {
        toast.info("1 minute completed. Moving to next question.");
        moveToQuestion(currentIndex + 1);
      } else {
        toast.info("1 minute completed. Please submit your answers.");
      }

      return undefined;
    }

    const timer = setTimeout(() => {
      setTimeLeft((prev) => prev - 1);
    }, 1000);

    return () => clearTimeout(timer);
  }, [currentIndex, loading, questions.length, timeLeft]);

  const moveToQuestion = (index) => {
    setCurrentIndex(index);
    setTimeLeft(QUESTION_TIME_LIMIT);
  };

  const isQuestionAnswered = (questionId) => (answers[questionId] || []).length > 0;

  const getAnsweredCount = () =>
    questions.filter((question) => isQuestionAnswered(question.ques_id)).length;

  const handleChange = (questionId, value, type) => {
    if (type === 1 || type === 2) {
      setAnswers((prev) => ({
        ...prev,
        [questionId]: [value],
      }));
      return;
    }

    if (type === 3 || type === 4) {
      const existing = answers[questionId] || [];

      if (existing.includes(value)) {
        setAnswers((prev) => ({
          ...prev,
          [questionId]: existing.filter((item) => item !== value),
        }));
      } else {
        setAnswers((prev) => ({
          ...prev,
          [questionId]: [...existing, value],
        }));
      }
    }
  };

  const submitAnswers = async () => {
    const unansweredQuestions = questions.filter((question) => !isQuestionAnswered(question.ques_id));

    if (unansweredQuestions.length > 0) {
      toast.error("Please answer all questions before submit");

      const unansweredIndex = questions.findIndex(
        (question) => question.ques_id === unansweredQuestions[0].ques_id
      );

      if (unansweredIndex >= 0) {
        moveToQuestion(unansweredIndex);
      }

      return;
    }

    setLoading(true);

    try {
      await axios.post(
        `${API_BASE_URL}/submit-post-test`,
        {
          training_participate_id: trainingId,
          question_count: questions.length,
          answers: answers,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
        }
      );

      toast.success("Post Test Submitted");

      setTimeout(() => {
        history.push("/TrainingParticipateList");
      }, 2000);
    } catch (err) {
      toast.error("Submit failed");
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await submitAnswers();
  };

  const currentQuestion = questions[currentIndex];
  const answeredCount = getAnsweredCount();
  const isLastQuestion = currentIndex === questions.length - 1;
  const progressPercentage = questions.length
    ? Math.round((answeredCount / questions.length) * 100)
    : 0;

  const formatTime = (seconds) => {
    const minutes = String(Math.floor(seconds / 60)).padStart(2, "0");
    const remainingSeconds = String(seconds % 60).padStart(2, "0");
    return `${minutes}:${remainingSeconds}`;
  };

  return (
    <>
      <ToastContainer />

      <div className="container-fluid">
        <div className="card">
          <div className="card-header d-flex flex-wrap justify-content-between align-items-center">
            <h4 className="mb-0">Post Test Exam Quiz</h4>
            <div className="text-right">
              <div>
                Answered: {answeredCount} / {questions.length}
              </div>
              <div className={`font-weight-bold ${timeLeft <= 10 ? "text-danger" : "text-primary"}`}>
                Time Left: {formatTime(timeLeft)}
              </div>
            </div>
          </div>

          <div className="card-body">
            {questions.length > 0 && (
              <>
                <div className="mb-4">
                  <div className="d-flex justify-content-between mb-2">
                    <span>Progress</span>
                    <span>{progressPercentage}%</span>
                  </div>
                  <div className="progress" style={{ height: "10px" }}>
                    <div
                      className="progress-bar bg-success"
                      role="progressbar"
                      style={{ width: `${progressPercentage}%` }}
                      aria-valuenow={progressPercentage}
                      aria-valuemin="0"
                      aria-valuemax="100"
                    />
                  </div>
                </div>

                <div className="mb-4">
                  <h6 className="mb-3">Question Panel</h6>
                  <div className="d-flex flex-wrap">
                    {questions.map((question, index) => (
                      <button
                        key={question.ques_id}
                        type="button"
                        className={`btn btn-sm mr-2 mb-2 ${
                          index === currentIndex
                            ? "btn-primary"
                            : isQuestionAnswered(question.ques_id)
                            ? "btn-success"
                            : "btn-outline-secondary"
                        }`}
                        onClick={() => moveToQuestion(index)}
                      >
                        {index + 1}
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}

            {currentQuestion ? (
              <form onSubmit={handleSubmit}>
                <div className="card border mb-3">
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-start mb-3">
                      <h5 className="mb-0">
                        Question {currentIndex + 1} of {questions.length}
                      </h5>
                      <span className="badge badge-info p-2">1 Minute Per Question</span>
                    </div>

                    <h6 className="mb-4">{currentQuestion.questions}</h6>

                    {currentQuestion.answers?.map((answer) => {
                      const selectedAnswers = answers[currentQuestion.ques_id] || [];
                      const isSingleSelect =
                        currentQuestion.answertype === 1 || currentQuestion.answertype === 2;

                      return (
                        <label
                          key={answer.id}
                          className="d-flex align-items-start border rounded p-3 mb-3"
                          style={{ cursor: "pointer", gap: "10px" }}
                        >
                          <input
                            type={isSingleSelect ? "radio" : "checkbox"}
                            name={`question_${currentQuestion.ques_id}`}
                            value={answer.id}
                            checked={selectedAnswers.includes(answer.id)}
                            onChange={() =>
                              handleChange(currentQuestion.ques_id, answer.id, currentQuestion.answertype)
                            }
                            style={{ marginTop: "4px" }}
                          />
                          <span>{answer.answer}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>

                <div className="d-flex flex-wrap justify-content-between">
                  <button
                    type="button"
                    className="btn btn-secondary mb-2"
                    disabled={currentIndex === 0 || loading}
                    onClick={() => moveToQuestion(currentIndex - 1)}
                  >
                    Previous Question
                  </button>

                  <div className="d-flex flex-wrap">
                    {!isLastQuestion && (
                      <button
                        type="button"
                        className="btn btn-primary mb-2 mr-2"
                        disabled={loading}
                        onClick={() => moveToQuestion(currentIndex + 1)}
                      >
                        Next Question
                      </button>
                    )}

                    <button type="submit" className="btn btn-success mb-2" disabled={loading}>
                      {loading ? "Submitting..." : "Submit All Answers"}
                    </button>
                  </div>
                </div>
              </form>
            ) : (
              <div className="text-center py-4">No questions available</div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default PostTestExamQuiz;
